import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BellRing,
  BookOpen,
  Check,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Copy,
  Gamepad2,
  Hourglass,
  Plus,
  Share,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  SquarePlus,
  Trash2,
  Trophy,
  X,
} from "lucide-react";
import { arcadeConfigured, createPrivateGame, deleteGame, getArcade, joinPrivateGame, roomParticipants } from "./lib/arcade";
import { isStandalone, mobilePlatform } from "./lib/install";
import { gravatarHash, gravatarUrl, normalizeEmail } from "./lib/gravatar";
import { enablePushNotifications, getPushSetupState, type PushSetupState } from "./lib/push-notifications";
import { deleteMatch, getProfile, listMatches, saveMatch, saveProfile } from "./lib/storage";
import { syncMatch } from "./lib/matchSync";
import { persistMatchBeforeTransition } from "./lib/matchHandoff";
import { GAMES, gameById, playableGames, type GameEntry } from "./games/registry";
import type { MatchSummaryPatch } from "./games/game-core";
import type { MatchSummary, Profile } from "./types";

type Route =
  | { name: "games" }
  | { name: "profile" }
  | { name: "rules-index" }
  | { name: "rules"; gameId: string }
  | { name: "table"; roomId: string };

type Sheet = "new" | "join" | "code" | null;

const routeFromLocation = (): Route => {
  const path = location.pathname;
  if (path === "/rules" || path === "/rules/") return { name: "rules-index" };
  if (path.startsWith("/rules/")) return { name: "rules", gameId: path.slice(7) || "othello" };
  if (path.startsWith("/games/")) return { name: "table", roomId: decodeURIComponent(path.slice(7)) };
  if (path === "/profile") return { name: "profile" };
  return { name: "games" };
};

const navigate = (path: string) => {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const expiresForGame = (gameId: string) => new Date(Date.now() + (gameId === "luna-genesis" ? 90 : 14) * 86_400_000).toISOString();
const DEFAULT_GAME = playableGames()[0]?.gameId ?? "othello";
// How often the games list re-checks each match's turn state while it's the visible screen.
const LIST_SYNC_INTERVAL_MS = 5 * 60_000;

export function App() {
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined);
  const [matches, setMatches] = useState<MatchSummary[]>([]);
  const [route, setRoute] = useState<Route>(routeFromLocation);

  // Async writers (move persist, participant sync, server confirmation) resolve out of order, and
  // replacing the whole array from a closure lets a slow response clobber a newer status with a
  // stale snapshot — e.g. reverting a just-played game to "your-turn" until the next poll. All of
  // them go through this patch instead: it merges only the fields the caller owns into the latest
  // matches (via a ref updated eagerly, so same-tick patches stack), and persists the result.
  const matchesRef = useRef<MatchSummary[]>([]);
  matchesRef.current = matches;
  const patchMatch = useCallback((roomId: string, fields: Partial<MatchSummary>): MatchSummary | undefined => {
    const existing = matchesRef.current.find((m) => m.roomId === roomId);
    if (!existing) return undefined;
    const merged = { ...existing, ...fields };
    matchesRef.current = matchesRef.current.map((m) => (m.roomId === roomId ? merged : m));
    void saveMatch(merged);
    setMatches(matchesRef.current);
    return merged;
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(routeFromLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!profile || !arcadeConfigured) return;
    void Promise.all(playableGames().map((game) => getArcade(game.gameId, profile.displayName, profile.gravatarHash))).catch(() => undefined);
  }, [profile]);

  useEffect(() => {
    void Promise.all([getProfile(), listMatches()]).then(([savedProfile, savedMatches]) => {
      setProfile(savedProfile);
      setMatches(savedMatches);
    });
  }, []);

  useEffect(() => {
    const removeDeletedMatch = (roomId: string) => {
      void deleteMatch(roomId);
      setMatches((current) => current.filter((match) => match.roomId !== roomId));
    };
    const deletedRoomId = new URLSearchParams(location.search).get("deleted");
    if (deletedRoomId) {
      removeDeletedMatch(deletedRoomId);
      history.replaceState({}, "", location.pathname);
    }
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "game.deleted" && typeof event.data.roomId === "string") removeDeletedMatch(event.data.roomId);
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, []);

  const fullscreen = route.name === "table" || route.name === "rules";

  const body =
    profile === undefined ? (
      <LoadingScreen />
    ) : !profile ? (
      <ProfileSetup onComplete={(saved) => setProfile(saved)} />
    ) : (
      <div className="app-frame">
        <main className={fullscreen ? "app-content fullscreen" : "app-content"}>
          {route.name === "games" && (
            <GamesScreen profile={profile} matches={matches} onMatchesChange={setMatches} onMatchPatch={patchMatch} />
          )}
          {route.name === "profile" && <ProfileScreen profile={profile} onProfileChange={setProfile} />}
          {route.name === "rules-index" && <RulesScreen />}
          {route.name === "rules" && <RulesRoute gameId={route.gameId} />}
          {route.name === "table" && (
            <TableRoute roomId={route.roomId} profile={profile} matches={matches} onMatchPatch={patchMatch} />
          )}
        </main>
        {!fullscreen && <BottomNav route={route} />}
      </div>
    );

  return (
    <>
      {body}
      <InstallPrompt />
    </>
  );
}

// Nudges iPhone visitors to install Turnstyle to the Home Screen, which iOS requires before it will
// deliver turn alerts (Web Push is only available to installed apps there). Android already supports
// push in a browser tab, so it is intentionally left alone. Hidden on desktop and when already
// running standalone. Dismissal lasts for the visit only — a fresh visit re-prompts.
function InstallPrompt() {
  const [show] = useState(() => !isStandalone() && mobilePlatform() === "ios");
  const [dismissed, setDismissed] = useState(false);

  if (!show || dismissed) return null;

  return (
    <div className="sheet-backdrop" role="presentation">
      <section className="game-sheet install-sheet" role="dialog" aria-modal="true" aria-labelledby="install-title">
        <button className="sheet-close" onClick={() => setDismissed(true)} aria-label="Continue in browser"><X /></button>
        <span className="install-badge" aria-hidden="true"><Smartphone /></span>
        <p className="eyebrow">Add to Home Screen</p>
        <h2 id="install-title">Install Turnstyle to get turn alerts</h2>
        <p className="sheet-copy">
          iPhone only sends notifications to installed apps. Add Turnstyle to your Home Screen so you’re
          alerted the moment it’s your move.
        </p>

        <ol className="install-steps">
          <li><span className="install-step-icon"><Share /></span><span>Tap the <strong>Share</strong> button in Safari’s toolbar.</span></li>
          <li><span className="install-step-icon"><SquarePlus /></span><span>Choose <strong>Add to Home Screen</strong>.</span></li>
          <li><span className="install-step-icon"><Check /></span><span>Tap <strong>Add</strong>, then open Turnstyle from your Home Screen.</span></li>
        </ol>

        <button className="text-button" onClick={() => setDismissed(true)}>Continue in browser</button>
      </section>
    </div>
  );
}

function RulesRoute({ gameId }: { gameId: string }) {
  const entry = gameById(gameId) ?? gameById("othello")!;
  return <entry.Rules onBack={() => navigate("/rules")} />;
}

function RulesScreen() {
  return (
    <>
      <header className="screen-header no-leading-action">
        <div><p className="eyebrow">How to play</p><h1>Rules</h1></div>
      </header>
      <section className="rules-library" aria-labelledby="rules-library-heading">
        <div className="section-heading">
          <h2 id="rules-library-heading">Choose a game</h2>
          <span>{GAMES.length} {GAMES.length === 1 ? "game" : "games"}</span>
        </div>
        <div className="rules-game-list">
          {GAMES.map((game) => (
            <button key={game.gameId} className="game-choice" onClick={() => navigate(`/rules/${game.gameId}`)}>
              <span className={`game-glyph ${game.gameId}`} aria-hidden="true" />
              <span className="game-choice-text"><strong>{game.displayName}</strong><small>{game.tagline}</small></span>
              <ChevronRight />
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

function TableRoute({
  roomId,
  profile,
  matches,
  onMatchPatch,
}: {
  roomId: string;
  profile: Profile;
  matches: MatchSummary[];
  onMatchPatch: (roomId: string, fields: Partial<MatchSummary>) => MatchSummary | undefined;
}) {
  const match = matches.find((m) => m.roomId === roomId);
  const entry = match ? gameById(match.gameId) : undefined;

  if (!match || !entry?.Table) {
    return (
      <div className="empty-games">
        <h3>Game not found</h3>
        <p>This match isn’t on this device, or the game isn’t available.</p>
        <button onClick={() => navigate("/games")}>Back to games</button>
      </div>
    );
  }

  // Once per room: fill in the opponent's real name/avatar. Only those fields — this fetch
  // races with move persists, so it must never write back a stale status or score.
  useEffect(() => {
    void roomParticipants(match.gameId, match.roomId, match.roomToken, profile.displayName, profile.gravatarHash).then(({ participants }) => {
      const opponents = participants.filter((participant) => participant.displayName !== profile.displayName);
      const opponent = opponents[0];
      if (!opponent) return;
      onMatchPatch(roomId, {
        opponentName: opponents.length > 1 ? `${opponent.displayName} + ${opponents.length - 1} more` : opponent.displayName,
        ...(opponent.avatarHash ? { opponentAvatarUrl: gravatarUrl(opponent.avatarHash, 96) } : {}),
      });
    }).catch(() => undefined);
  }, [match.gameId, match.roomId, match.roomToken, onMatchPatch, profile.displayName, profile.gravatarHash, roomId]);

  const persist = (patch: MatchSummaryPatch) => {
    const wasMyTurn = match.status === "your-turn";
    const updated = onMatchPatch(roomId, {
      myScore: patch.myScore,
      opponentScore: patch.opponentScore,
      status: patch.status,
      result: patch.result === "won" || patch.result === "lost" || patch.result === "draw" || patch.result === "expired" ? patch.result : match.result,
      updatedAt: new Date().toISOString(),
    });
    // Just handed the turn over: confirm against the server that the move was recorded and
    // correct the summary if it disagrees (e.g. the deadline lapsed and the server skipped us).
    if (updated && wasMyTurn && patch.status === "waiting") {
      void syncMatch(updated).then((confirmed) => {
        if (confirmed) onMatchPatch(roomId, { myScore: confirmed.myScore, opponentScore: confirmed.opponentScore, status: confirmed.status, result: confirmed.result, updatedAt: confirmed.updatedAt });
      }).catch(() => undefined);
    }
  };

  return (
    <entry.Table
      roomId={match.roomId}
      roomToken={match.roomToken}
      myName={profile.displayName}
      opponentName={match.opponentName}
      opponentAvatarUrl={match.opponentAvatarUrl}
      onExit={() => navigate("/games")}
      onProgress={persist}
    />
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen" role="status">
      <BrandMark />
      <span>Setting the table…</span>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="brand-mark" aria-hidden="true">
      <span>T</span>
      <i />
    </div>
  );
}

function ProfileSetup({ onComplete }: { onComplete: (profile: Profile) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !email.includes("@")) {
      setError("Enter your name and a valid email address.");
      return;
    }
    setBusy(true);
    const now = new Date().toISOString();
    try {
      const profile: Profile = {
        version: 1,
        profileId: crypto.randomUUID(),
        displayName: name.trim(),
        email: normalizeEmail(email),
        gravatarHash: await gravatarHash(email),
        createdAt: now,
        updatedAt: now,
      };
      await saveProfile(profile);
      onComplete(profile);
    } catch {
      setError("Your profile could not be saved on this device.");
      setBusy(false);
    }
  };

  return (
    <main className="onboarding">
      <div className="onboarding-glow" />
      <section className="onboarding-card">
        <BrandMark />
        <p className="eyebrow">Play a turn when it suits you</p>
        <h1>Welcome to<br /><em>Turnstyle</em></h1>
        <p className="intro">A quiet table for games with people you know. No account or password—just your name and a shared game code.</p>

        <form onSubmit={submit} className="profile-form">
          <label>
            <span>Your name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={24} autoComplete="name" placeholder="Sarah" />
          </label>
          <label>
            <span>Email for your Gravatar</span>
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" placeholder="you@example.com" />
          </label>
          <p className="privacy-note"><ShieldCheck size={16} /> Your email stays on this device. Its hash is shared with Gravatar and opponents to show your picture.</p>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button" disabled={busy}>{busy ? "Saving…" : "Take a seat"}</button>
        </form>
      </section>
    </main>
  );
}

function GamesScreen({
  profile,
  matches,
  onMatchesChange,
  onMatchPatch,
}: {
  profile: Profile;
  matches: MatchSummary[];
  onMatchesChange: (matches: MatchSummary[]) => void;
  onMatchPatch: (roomId: string, fields: Partial<MatchSummary>) => MatchSummary | undefined;
}) {
  const [filter, setFilter] = useState<MatchSummary["status"]>("your-turn");
  const [sheet, setSheet] = useState<Sheet>(null);
  const [activeCode, setActiveCode] = useState("");
  const [activeRoomId, setActiveRoomId] = useState("");
  const counts = useMemo(() => ({
    "your-turn": matches.filter((match) => match.status === "your-turn").length,
    waiting: matches.filter((match) => match.status === "waiting").length,
    finished: matches.filter((match) => match.status === "finished").length,
  }), [matches]);
  const visible = matches.filter((match) => match.status === filter);

  // The list loads summaries once, so an opponent's move (or a finished game) wouldn't reach a card
  // until it was opened. While the list is on screen, re-derive each match's status from the server:
  // immediately, whenever the app returns to the foreground, and on a light poll for turns taken
  // while it's being watched. A ref feeds the latest matches in without re-subscribing each render.
  const matchesRef = useRef(matches);
  matchesRef.current = matches;
  useEffect(() => {
    if (!arcadeConfigured) return;
    let cancelled = false;
    const syncAll = () => {
      if (document.visibilityState !== "visible") return;
      void Promise.all(matchesRef.current.map((match) => syncMatch(match).catch(() => null))).then((results) => {
        if (cancelled) return;
        // Patch only the fields the server sync derives — patchMatch merges into the latest state
        // (and no-ops for since-deleted rooms), so slow responses can't clobber fresher writes.
        for (const updated of results) {
          if (updated) onMatchPatch(updated.roomId, { myScore: updated.myScore, opponentScore: updated.opponentScore, status: updated.status, result: updated.result, updatedAt: updated.updatedAt });
        }
      });
    };
    syncAll();
    const interval = window.setInterval(syncAll, LIST_SYNC_INTERVAL_MS);
    const onForeground = () => { if (document.visibilityState === "visible") syncAll(); };
    document.addEventListener("visibilitychange", onForeground);
    window.addEventListener("focus", onForeground);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onForeground);
      window.removeEventListener("focus", onForeground);
    };
  }, [onMatchPatch]);

  const handoffMatch = (match: MatchSummary, transition: () => void) =>
    persistMatchBeforeTransition(match, matchesRef.current, saveMatch, onMatchesChange, transition);

  return (
    <>
      <header className="screen-header no-leading-action">
        <div>
          <p className="eyebrow">Turnstyle</p>
          <h1>Games</h1>
        </div>
        <button className="avatar-button" onClick={() => navigate("/profile")} aria-label="Open profile">
          <img src={gravatarUrl(profile.gravatarHash, 96)} alt="" />
          <span className="presence-dot" />
        </button>
      </header>

      <section className="game-actions" aria-label="Game actions">
        <button className="new-game-button" onClick={() => setSheet("new")}><Plus /> New game</button>
        <button className="code-button" onClick={() => setSheet("join")}><Gamepad2 /> Enter game code</button>
      </section>

      {!arcadeConfigured && (
        <div className="setup-banner"><Sparkles size={18} /><span>The table is ready. Set <code>VITE_ARCADE_ENDPOINT</code> to connect the game server.</span></div>
      )}

      <div className="filter-rail" role="tablist" aria-label="Filter games">
        <FilterButton label="Your turn" icon={<Clock3 />} count={counts["your-turn"]} active={filter === "your-turn"} onClick={() => setFilter("your-turn")} />
        <FilterButton label="Waiting" icon={<Hourglass />} count={counts.waiting} active={filter === "waiting"} onClick={() => setFilter("waiting")} />
        <FilterButton label="Finished" icon={<Check />} count={counts.finished} active={filter === "finished"} onClick={() => setFilter("finished")} />
      </div>

      <section className="games-section">
        <div className="section-heading">
          <h2>{filter === "your-turn" ? "Ready for you" : filter === "waiting" ? "Across the table" : "On this device"}</h2>
          <span>{visible.length} {visible.length === 1 ? "game" : "games"}</span>
        </div>
        {visible.length ? visible.map((match) => <MatchCard key={match.roomId} match={match} myName={profile.displayName} onOpen={() => navigate(`/games/${encodeURIComponent(match.roomId)}`)} onDelete={async () => {
          if (!confirm(`Delete this game with ${match.opponentName}? This removes it for everyone.`)) return;
          try { await deleteGame(match.gameId, match.roomId, match.roomToken, profile.displayName, profile.gravatarHash); } catch { /* The room may already have expired. */ }
          await deleteMatch(match.roomId);
          onMatchesChange(matches.filter((item) => item.roomId !== match.roomId));
        }} />) : <EmptyGames filter={filter} onNew={() => setSheet(filter === "waiting" ? "join" : "new")} />}
      </section>

      {sheet && (
        <GameSheet
          mode={sheet}
          displayName={profile.displayName}
          avatarHash={profile.gravatarHash}
          code={activeCode}
          roomId={activeRoomId}
          onClose={() => setSheet(null)}
          onCreated={async (code, match) => {
            await handoffMatch(match, () => {
              setActiveCode(code);
              setActiveRoomId(match.roomId);
              setSheet("code");
            });
          }}
          onJoined={async (match) => {
            await handoffMatch(match, () => {
              setSheet(null);
              navigate(`/games/${encodeURIComponent(match.roomId)}`);
            });
          }}
          onOpenRoom={(roomId) => {
            setSheet(null);
            navigate(`/games/${encodeURIComponent(roomId)}`);
          }}
        />
      )}
    </>
  );
}

function FilterButton({ label, icon, count, active, onClick }: { label: string; icon: React.ReactNode; count: number; active: boolean; onClick: () => void }) {
  return <button role="tab" aria-selected={active} className={active ? "active" : ""} onClick={onClick}>{icon}<span>{label}</span><b>{count}</b></button>;
}

function MatchCard({ match, myName, onOpen, onDelete }: { match: MatchSummary; myName: string; onOpen: () => void; onDelete: () => void }) {
  const gameName = gameById(match.gameId)?.displayName ?? "Game";
  const turnName = match.status === "your-turn" ? myName : match.status === "waiting" ? match.opponentName : null;
  const title = `${gameName} game with ${match.opponentName}. ${turnName ? `It’s ${turnName}'s turn.` : "Game finished."}`;
  return (
    <article className="match-card" onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}>
      <div className="match-person">
        {match.opponentAvatarUrl ? <img src={match.opponentAvatarUrl} alt="" /> : <div className="avatar-placeholder"><CircleUserRound /></div>}
        <div><h3>{title}</h3><p><strong>{match.myScore}</strong><i>—</i>{match.opponentScore}</p></div>
        <time dateTime={match.updatedAt}>{new Date(match.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</time>
      </div>
      <div className="match-detail">
        <span className={`status-pill ${match.status}`}>{match.status === "your-turn" ? <><Clock3 /> Your turn</> : match.status === "waiting" ? <><Hourglass /> Waiting</> : <><Trophy /> {match.result === "won" ? "You won" : match.result === "lost" ? "You lost" : "Finished"}</>}</span>
        <div className="match-card-actions"><small>{gameName}</small><button className="delete-match" aria-label={`Delete ${gameName} game with ${match.opponentName}`} onClick={(event) => { event.stopPropagation(); void onDelete(); }}><Trash2 /></button></div>
      </div>
    </article>
  );
}

function EmptyGames({ filter, onNew }: { filter: MatchSummary["status"]; onNew: () => void }) {
  const content = filter === "your-turn"
    ? ["You’re caught up", "When a game comes back to you, it’ll land here.", "Start a game"]
    : filter === "waiting"
      ? ["Nothing across the table", "Enter a code to join someone you know.", "Enter a code"]
      : ["No finished games yet", "Completed games stay only on this device.", "Start a game"];
  return (
    <div className="empty-games">
      <div className="empty-deck"><i /><i /><i><span>TS</span></i></div>
      <h3>{content[0]}</h3><p>{content[1]}</p>
      <button onClick={onNew}>{content[2]}</button>
    </div>
  );
}

function GameSheet({ mode, displayName, avatarHash, code, roomId, onClose, onCreated, onJoined, onOpenRoom }: {
  mode: Sheet;
  displayName: string;
  avatarHash: string;
  code: string;
  roomId: string;
  onClose: () => void;
  onCreated: (code: string, match: MatchSummary) => Promise<void>;
  onJoined: (match: MatchSummary) => Promise<void>;
  onOpenRoom: (roomId: string) => void;
}) {
  const [joinCode, setJoinCode] = useState("");
  // A game chosen from the picker that still needs a seat count before we create it.
  const [pendingGame, setPendingGame] = useState<GameEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Games with selectable seat counts ask for the count first; two-player games create immediately.
  const chooseGame = (game: GameEntry) => (game.playerCounts ? setPendingGame(game) : void create(game.gameId));

  const create = async (gameId: string, playerCount?: number) => {
    setBusy(true); setError("");
    try {
      const room = await createPrivateGame(gameId, displayName, playerCount, avatarHash);
      if (!room.info.code) throw new Error("The game server did not return a code.");
      await onCreated(room.info.code, {
        roomId: room.info.roomId, roomToken: room.info.token, gameId, code: room.info.code,
        opponentName: gameId === "luna-genesis" ? "Waiting for players" : "Waiting for player", myScore: 0, opponentScore: 0, status: gameId === "luna-genesis" ? "waiting" : "your-turn",
        updatedAt: new Date().toISOString(), expiresAt: expiresForGame(gameId),
      });
    } catch (cause) { setError(cause instanceof Error ? cause.message : "The game could not be created."); setBusy(false); }
  };

  const join = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const room = await joinPrivateGame(undefined, joinCode, displayName, avatarHash);
      const gameId = room.info.gameId ?? DEFAULT_GAME;
      await onJoined({
        roomId: room.info.roomId, roomToken: room.info.token, gameId,
        opponentName: "Opponent", myScore: 0, opponentScore: 0, status: "waiting",
        updatedAt: new Date().toISOString(), expiresAt: expiresForGame(gameId),
      });
    } catch (cause) { setError(cause instanceof Error ? cause.message : "That code could not be joined."); setBusy(false); }
  };

  const copyCode = async () => { await navigator.clipboard.writeText(code); };
  const shareCode = async () => {
    if (navigator.share) await navigator.share({ title: "Join my Turnstyle game", text: `Enter game code ${code} in Turnstyle.` });
    else await copyCode();
  };

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className="game-sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-title">
        <button className="sheet-close" onClick={onClose} aria-label="Close"><X /></button>

        {mode === "new" && !pendingGame && (
          <>
            <p className="eyebrow">Private table</p>
            <h2 id="sheet-title">Start a new game</h2>
            <div className="game-picker">
              {GAMES.map((game) => (
                <button
                  key={game.gameId}
                  className={`game-choice${game.playable ? "" : " disabled"}`}
                  disabled={!game.playable || busy}
                  onClick={() => game.playable && chooseGame(game)}
                >
                  <span className={`game-glyph ${game.gameId}`} aria-hidden="true" />
                  <span className="game-choice-text"><strong>{game.displayName}</strong><small>{game.tagline}</small></span>
                  {game.playable ? <ChevronRight /> : <em className="soon-badge">Soon</em>}
                </button>
              ))}
            </div>
            <p className="sheet-copy">We’ll make a private code. Anyone with it can take an open seat.</p>
          </>
        )}

        {mode === "new" && pendingGame && (
          <>
            <p className="eyebrow">{pendingGame.displayName}</p>
            <h2 id="sheet-title">How many players?</h2>
            <div className="game-picker">
              {pendingGame.playerCounts!.map((count) => (
                <button
                  key={count}
                  className="game-choice"
                  disabled={busy}
                  onClick={() => void create(pendingGame.gameId, count)}
                >
                  <span className="game-glyph count-glyph" aria-hidden="true">{count}</span>
                  <span className="game-choice-text"><strong>{count} players</strong><small>{count === 2 ? "Head to head" : `${count} at the table`}</small></span>
                  <ChevronRight />
                </button>
              ))}
            </div>
            <p className="sheet-copy">Your game code fills every seat — share it with each player.</p>
            <button className="text-button" onClick={() => { setPendingGame(null); setError(""); }} disabled={busy}>Choose a different game</button>
          </>
        )}

        {mode === "join" && (
          <>
            <p className="eyebrow">Take the open seat</p>
            <h2 id="sheet-title">Enter game code</h2>
            <form onSubmit={join} className="join-form">
              <label>
                <span>Game code</span>
                <input value={joinCode} onChange={(event) => setJoinCode(event.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off" spellCheck={false} placeholder="OAB12CD" maxLength={24} autoFocus />
              </label>
              <p>The game type is included in the code.</p>
              <button className="primary-button" disabled={busy || joinCode.replace(/\s/g, "").length < 7}>{busy ? "Joining…" : "Join game"}</button>
            </form>
          </>
        )}

        {mode === "code" && (
          <>
            <p className="eyebrow">Your table is ready</p>
            <h2 id="sheet-title">Share this game code</h2>
            <div className="share-code" aria-label={`Game code ${code.split("").join(" ")}`}>{code}</div>
            <p className="sheet-copy">Everyone you invite enters this code in Turnstyle and joins immediately.</p>
            <div className="share-actions"><button onClick={copyCode}><Copy /> Copy code</button><button className="accent" onClick={shareCode}><Share2 /> Share</button></div>
            <button className="primary-button" onClick={() => onOpenRoom(roomId)}>Open game</button>
          </>
        )}

        {error && <p className="form-error" role="alert">{error}</p>}
      </section>
    </div>
  );
}

function ProfileScreen({ profile, onProfileChange }: { profile: Profile; onProfileChange: (profile: Profile) => void }) {
  void onProfileChange;
  const [notifications, setNotifications] = useState<PushSetupState>("off");
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  useEffect(() => { void getPushSetupState().then(setNotifications); }, []);
  const enableNotifications = async () => {
    setNotificationBusy(true);
    setNotificationError("");
    try {
      await enablePushNotifications(profile.displayName);
      setNotifications("on");
    } catch (cause) {
      setNotificationError(cause instanceof Error ? cause.message : "Turn alerts could not be enabled.");
      setNotifications(await getPushSetupState());
    } finally {
      setNotificationBusy(false);
    }
  };
  return (
    <>
      <header className="screen-header no-leading-action"><div><p className="eyebrow">This device</p><h1>Profile</h1></div><span /></header>
      <section className="profile-hero"><img src={gravatarUrl(profile.gravatarHash, 240)} alt={`${profile.displayName}’s Gravatar`} /><div><h2>{profile.displayName}</h2><p>Your profile lives on this device.</p></div></section>
      <section className="settings-group"><h2>Turn alerts</h2><button className="setting-row" onClick={() => void enableNotifications()} disabled={notificationBusy || notifications === "on" || notifications === "unsupported"}><span className="setting-icon"><BellRing /></span><span><strong>{notifications === "on" ? "Notifications enabled" : notifications === "unsupported" ? "Notifications unavailable" : "Enable notifications"}</strong><small>{notifications === "unsupported" ? "Install Turnstyle or use a browser that supports web push" : "Get an alert when a game comes back to you"}</small></span><b>{notificationBusy ? "Setting up…" : notifications === "on" ? "On" : notifications === "unsupported" ? "—" : "Set up"}</b></button>{notificationError && <p className="settings-error" role="alert">{notificationError}</p>}</section>
      <section className="settings-group"><h2>Help</h2>
        {playableGames().map((game) => (
          <button key={game.gameId} className="setting-row" onClick={() => navigate(`/rules/${game.gameId}`)}><span className="setting-icon"><BookOpen /></span><span><strong>How to play {game.displayName}</strong><small>{game.tagline}</small></span><ChevronRight className="row-chevron" /></button>
        ))}
      </section>
      <section className="privacy-card"><ShieldCheck /><div><strong>No Turnstyle account</strong><p>Your email remains on this device. We share its SHA-256 hash with Gravatar and your opponents to display your picture.</p></div></section>
    </>
  );
}

function BottomNav({ route }: { route: Route }) {
  return (
    <nav className="bottom-nav" aria-label="Primary navigation">
      <button className={route.name === "games" ? "active" : ""} onClick={() => navigate("/games")}><Gamepad2 /><span>Games</span></button>
      <button className={route.name === "rules-index" || route.name === "rules" ? "active" : ""} onClick={() => navigate("/rules")}><BookOpen /><span>Rules</span></button>
      <button className={route.name === "profile" ? "active" : ""} onClick={() => navigate("/profile")}><CircleUserRound /><span>Profile</span></button>
    </nav>
  );
}
